import { normalizeLocale, type SupportedLocale } from '../i18n/constants'

export const LUCAS_TTS_VOICE_KEY = 'lucas-tts-voice'
export const LUCAS_TTS_RATE_KEY = 'lucas-tts-rate'

export type LucasTtsVoice = 'female' | 'male'

/** Preset speak rates (Edge TTS rate = (rate - 1) × 100%). */
export const LUCAS_TTS_RATE_PRESETS = [0.75, 1, 1.25, 1.5, 1.75, 2] as const
export type LucasTtsRate = (typeof LUCAS_TTS_RATE_PRESETS)[number]

export const DEFAULT_LUCAS_TTS_VOICE: LucasTtsVoice = 'female'
export const DEFAULT_LUCAS_TTS_RATE: LucasTtsRate = 1

export function getLucasTtsVoice(): LucasTtsVoice {
  if (typeof window === 'undefined') return DEFAULT_LUCAS_TTS_VOICE
  try {
    const raw = window.localStorage.getItem(LUCAS_TTS_VOICE_KEY)
    if (raw === 'male' || raw === 'female') return raw
  } catch {
    /* ignore */
  }
  return DEFAULT_LUCAS_TTS_VOICE
}

export function setLucasTtsVoice(voice: LucasTtsVoice): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LUCAS_TTS_VOICE_KEY, voice)
  } catch {
    /* ignore */
  }
}

export function isLucasTtsRate(value: unknown): value is LucasTtsRate {
  return (
    typeof value === 'number' &&
    (LUCAS_TTS_RATE_PRESETS as readonly number[]).includes(value)
  )
}

export function parseLucasTtsRate(raw: string | null | undefined): LucasTtsRate | null {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  return isLucasTtsRate(n) ? n : null
}

export function getLucasTtsRate(): LucasTtsRate {
  if (typeof window === 'undefined') return DEFAULT_LUCAS_TTS_RATE
  try {
    const parsed = parseLucasTtsRate(window.localStorage.getItem(LUCAS_TTS_RATE_KEY))
    if (parsed != null) return parsed
  } catch {
    /* ignore */
  }
  return DEFAULT_LUCAS_TTS_RATE
}

export function setLucasTtsRate(rate: LucasTtsRate): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(LUCAS_TTS_RATE_KEY, String(rate))
  } catch {
    /* ignore */
  }
}

/** Display label e.g. 1 → "1×", 1.5 → "1.5×" (language-neutral). */
export function formatLucasTtsRateLabel(rate: number): string {
  const text = Number.isInteger(rate) ? String(rate) : String(rate)
  return `${text}×`
}

/**
 * @deprecated Speak language is detected from text on the backend.
 * Kept for callers that still need a locale code; do not send as Speak language.
 */
export function localeToSpeakLanguage(locale?: string | null): SupportedLocale {
  return normalizeLocale(locale)
}

/** @deprecated Use localeToSpeakLanguage */
export const localeToMeloLanguage = localeToSpeakLanguage
